<template>
    <div
        :class="[
            'list-container',
            variant,
            hasIcon ? 'has-icon' : 'no-icon',
            {
                disabled,
                inactive,
                hidden,
                iconSpacer,
                microWrap,
                noGlyph: !$slots.glyph,
                'segment-control': segmentControl,
                'is-selectable': isInteractive,
            },
        ]"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div class="list-row">
            <div v-if="curVariant === ComponentState.Selected" class="list-chip-area">
                <div class="list-chip" />
            </div>

            <div v-if="$slots.icon" class="list-icon-area">
                <slot name="icon" />
                <div v-if="notify" class="list-notify" />
            </div>

            <div v-if="iconSpacer" class="list-spacer-area" />

            <div class="list-text-container">
                <div class="list-macro">
                    <div class="list-macro-box">
                        <div class="list-macro-text">
                            <slot />
                        </div>
                        <slot name="data" :disabled="disabled" :variant="variant" />
                    </div>
                    <slot name="nano" />
                </div>
                <div class="list-micro-text">
                    <slot name="microtext" />
                </div>
            </div>

            <div v-if="$slots.glyph" class="list-glyph-area">
                <!-- CLICKABLE ROW for glyphs, timestamps, external links, nested views, etc -->
                <slot name="glyph" />
            </div>
        </div>

        <div class="list-controls">
            <div v-if="$slots.toggle" class="list-toggle-area">
                <!-- NON CLICKABLE ROW for row toggle switch rows (Similar to layer slot but aligns at start with microtext) -->
                <slot name="toggle" />
            </div>
            <div v-if="$slots.price" class="list-price-area">
                <!-- NON CLICKABLE ROW for price, supports being next to button slot -->
                <slot name="price" />
            </div>
            <div v-if="$slots.button" class="list-button-area">
                <!-- NON CLICKABLE ROW for row ButtonArea component, supports multiple buttons -->
                <slot name="button" />
            </div>
        </div>
        <div v-if="$slots.control" class="list-segment-controls">
            <!-- NON CLICKABLE ROW for segment controls beneath listRow -->
            <slot name="control" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, useSlots, provide, inject, toRef } from 'vue';
import { ComponentState } from '../composables/useConstants';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    // Hidden dims the icon but leaves the text at the default color
    hidden: {
        type: Boolean,
        default: false,
    },
    // Inactive dims the icon AND greys out the text, but keeps the row clickable
    inactive: {
        type: Boolean,
        default: false,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    iconSpacer: {
        type: Boolean,
        default: false,
    },
    notify: {
        type: Boolean,
        default: false,
    },
    microWrap: {
        type: Boolean,
        default: false,
    },
    variant: {
        type: String,
        default: 'default',
    },
    segmentControl: {
        type: Boolean,
        default: false,
    },
    selectable: {
        type: Boolean,
        default: false,
    },
});

const slots = useSlots();

const hasIcon = computed(() => {
    return (!!slots.icon && slots.icon().length > 0) || props.iconSpacer;
});

const isInteractive = computed(() => {
    return props.selectable || (!!slots.glyph && slots.glyph().length > 0);
});

const isDisabled = toRef(props, 'disabled');
const isInactive = toRef(props, 'inactive');

const curVariant = computed(() => props.variant);
const isHovered = ref(false);

const isChildHoverable = computed(() => {
    const isSelected = props.variant === ComponentState.Selected;
    return isHovered.value && isInteractive.value && !isSelected && !props.disabled;
});

const handleMouseEnter = () => {
    if (isInteractive.value && !props.disabled) {
        isHovered.value = true;
    }
};

const handleMouseLeave = () => {
    isHovered.value = false;
};

provide('disabled', isDisabled);
provide('inactive', isInactive);
provide('variant', curVariant);
provide('hover', isChildHoverable);
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.list-container {
    display: flex;
    width: 100%;
    position: relative;
    cursor: pointer;
    user-select: none;
    background: transparent;

    &.segment-control {
        flex-direction: column;

        .list-segment-controls {
            padding: 12px 16px;
            padding-top: 0;
        }
    }

    &.noGlyph {
        cursor: default;
    }

    .list-row {
        display: flex;
        flex-flow: row nowrap;
        align-items: flex-start;
        gap: Pathfinder.$space-200;
        width: 100%;
        padding: Pathfinder.$space-150 Pathfinder.$space-200;

        .list-icon-area {
            height: Pathfinder.$space-300;
            width: Pathfinder.$space-300;
            display: flex;
            justify-content: center;
            align-items: center;
            user-select: none;
            position: relative;
            flex-shrink: 0;

            fill: var(--on-surface);

            :deep(> svg),
            :deep(> * > svg) {
                width: 100%;
                height: 100%;
                max-width: 100%;
                max-height: 100%;
            }

            &:empty {
                display: none;
            }

            .list-notify {
                position: absolute;
                top: -(Pathfinder.$space-25);
                right: -(Pathfinder.$space-25);
                height: 10px;
                width: 10px;
                border-radius: Pathfinder.$radius-max;
                background: var(--brand);

                // this border should to be same as row background coming from master list
                border: Pathfinder.$space-25 solid var(--surface);
            }
        }

        .list-chip-area {
            position: absolute;
            left: 0;
            display: flex;
            align-items: center;
            height: Pathfinder.$space-300;

            .list-chip {
                position: absolute;
                background-color: var(--selection);
                height: Pathfinder.$space-150;
                width: Pathfinder.$space-50;
                border-top-right-radius: Pathfinder.$space-100;
                border-bottom-right-radius: Pathfinder.$space-100;
            }
        }

        .list-spacer-area {
            display: flex;
            height: Pathfinder.$space-300;
            width: Pathfinder.$space-300;
        }

        .list-text-container {
            display: flex;
            flex-direction: column;
            gap: Pathfinder.$space-25;

            flex: 1;
            width: 0;

            .list-macro {
                display: flex;
                justify-content: space-between;
                align-items: center;
                height: Pathfinder.$space-300;
                @include Pathfinder.Row-Nano;
                color: var(--on-surface-alt);

                .list-macro-box {
                    @include Pathfinder.Row-Macro;
                    display: flex;
                    align-items: center;
                    gap: Pathfinder.$space-100;
                    height: 100%;
                    color: var(--on-surface);
                    min-width: 0;

                    .list-macro-text {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }

            .list-micro-text {
                @include Pathfinder.Row-Micro;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                color: var(--on-surface-alt);

                &:empty {
                    display: none;
                }
            }
        }

        .list-glyph-area {
            @include Pathfinder.Legalcopy;
            display: flex;
            height: Pathfinder.$space-300;
            justify-content: center;
            align-items: center;
            flex-shrink: 0;
            gap: Pathfinder.$space-200;

            fill: var(--on-surface-alt);

            &:empty {
                display: none;
            }
        }
    }

    &.microWrap {
        .list-row {
            .list-micro-text {
                white-space: normal;
                word-break: break-word;
            }
        }
    }

    .list-controls {
        display: flex;
        padding-right: Pathfinder.$space-50;

        .list-toggle-area {
            display: flex;
            height: 100%;
            justify-content: center;
            align-items: center;
            padding-right: Pathfinder.$space-150;
        }

        .list-button-area {
            display: flex;
            height: 100%;
            justify-content: center;
            align-items: center;
        }

        .list-price-area {
            @include Pathfinder.Macro-Text;
            font-weight: 600;
            display: flex;
            height: 100%;
            justify-content: center;
            align-items: center;
            margin-right: Pathfinder.$space-150;
        }

        &:empty {
            display: none;
        }
    }

    &.has-icon::before,
    &.iconSpacer::before {
        left: Pathfinder.$space-700; // Adjusted for when the icon is present
        width: calc(100% - Pathfinder.$space-700);
    }

    &.no-icon::before {
        left: Pathfinder.$space-200; // Adjusted for when the icon is not present
        width: calc(100% - Pathfinder.$space-200);
    }

    &.iconSpacer::before {
        left: Pathfinder.$space-700; // Adjusted for when the icon-spacer is present
        width: calc(100% - Pathfinder.$space-700);
    }

    ///////////// ERROR ///////////////
    &.error {
        background: var(--critical-glowbox);

        .list-row {
            .list-macro {
                color: var(--on-critical-glowbox-alt);

                .list-macro-text {
                    color: var(--on-critical-glowbox);
                }
            }

            .list-micro-text {
                color: var(--on-critical-glowbox-alt);
            }
        }

        .list-glyph-area {
            fill: var(--on-critical-glowbox-alt);
        }
    }

    ///////////// SUCCESS ///////////////
    &.success {
        background: var(--success-glowbox);

        .list-row {
            .list-macro {
                color: var(--on-success-glowbox-alt);

                .list-macro-text {
                    color: var(--on-success-glowbox);
                }
            }

            .list-micro-text {
                color: var(--on-success-glowbox-alt);
            }
        }

        .list-glyph-area {
            fill: var(--on-success-glowbox-alt);
        }
    }

    // HOVER
    &.is-selectable:not(.disabled):not(.selected):hover {
        background: var(--action-glowbox);

        .list-row {
            .list-macro {
                color: var(--on-action-glowbox-alt);

                .list-macro-text {
                    color: var(--on-action-glowbox);
                }
            }

            .list-micro-text {
                color: var(--on-action-glowbox-alt);
            }
        }

        .list-glyph-area {
            fill: var(--on-action-glowbox-alt);
        }
    }

    ///////////// INACTIVE ///////////////
    // Still clickable: keeps the glyph and hover state (hover restores the text colours)
    &.inactive {
        .list-row {
            .list-icon-area {
                @include Pathfinder.PrimaryOpacity;
            }

            .list-macro .list-macro-text {
                color: var(--on-surface-alt);
            }
        }

        &.error .list-row .list-macro .list-macro-text {
            color: var(--on-critical-glowbox-alt);
        }

        &.success .list-row .list-macro .list-macro-text {
            color: var(--on-success-glowbox-alt);
        }
    }

    ///////////// HIDDEN ///////////////
    &.hidden {
        .list-row .list-icon-area {
            @include Pathfinder.PrimaryOpacity;
        }
    }

    ///////////// DISABLED ///////////////
    &.disabled {
        background: var(--surface-border);
        cursor: auto;

        .list-row {
            .list-icon-area {
                fill: var(--on-surface-alt);
            }

            .list-macro {
                color: var(--on-surface-alt);

                .list-macro-text {
                    color: var(--on-surface-alt);
                }
            }

            .list-micro-text {
                color: var(--on-surface-alt);
            }
        }

        .list-glyph-area {
            display: none;
            fill: var(--on-surface-alt);
        }
    }

    ///////////// SELECTED ///////////////
    &.selected {
        background: var(--selection-glowbox);
        cursor: auto;

        .list-row {
            .list-macro {
                color: var(--on-selection-glowbox-alt);

                .list-macro-text {
                    color: var(--on-selection-glowbox);
                    font-weight: 600;
                }
            }

            .list-micro-text {
                color: var(--on-selection-glowbox-alt);
            }
        }

        .list-glyph-area {
            display: none;
        }
    }
}

.list-container.is-selectable:not(.disabled) {
    cursor: pointer;
}

.list-container + .list-container {
    position: relative;
}

.list-container + .list-container::before {
    content: '';
    position: absolute;
    top: 0;
    height: 1px;
    background-color: var(--surface-border);
    cursor: auto;
}
</style>
